import React from 'react';
import { motion } from 'framer-motion';
import { XCircle, AlertCircle, CheckCircle2 } from 'lucide-react';
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 40
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};
export function SalesContent() {
  return <>
      {/* OPENING HOOK */}
      <section className="py-20 px-4 md:px-6 bg-[#fdfcfa] text-[#1e2d4d]" data-id="element-25">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={fadeInUp} className="max-w-3xl mx-auto" data-id="element-26">
          <div className="prose prose-lg mx-auto text-[#1e2d4d]" data-id="element-27">
            <p className="text-xl md:text-2xl font-serif italic leading-relaxed mb-8 border-l-4 border-[#c9a84c] pl-6 py-2 bg-white shadow-sm rounded-r-lg" data-id="element-28">
              "Son las 8 de la noche de un domingo. Sientes ese nudo en el
              estómago porque sabes que mañana te esperan 10 horas de pie,
              limpiando casas, cocinando o cargando cajas..."
            </p>

            <p className="mb-6" data-id="element-29">
              <span className="font-bold text-red-700" data-id="element-30">
                Te duelen las rodillas
              </span>
              ,{' '}
              <span className="font-bold text-red-700" data-id="element-31">
                te duele la espalda
              </span>{' '}
              y, sobre todo, te duele sentir que tu vida se escapa en un trabajo
              que te agota el cuerpo pero no te llena el bolsillo.
            </p>

            <p className="mb-6" data-id="element-32">
              Miras tu teléfono y ves a otros trabajando desde su computadora.
              Piensas:{' '}
              <span className="italic text-gray-600" data-id="element-33">
                "Eso no es para mí, yo no hablo inglés perfecto"
              </span>{' '}
              o{' '}
              <span className="italic text-gray-600" data-id="element-34">
                "Yo no sé de tecnología"
              </span>
              .
            </p>

            <p className="text-xl font-bold text-[#1e2d4d] bg-[#c9a84c]/10 p-4 rounded-lg text-center" data-id="element-35">
              Estás cometiendo el error que te mantiene atada al cansancio
              físico.
            </p>
          </div>
        </motion.div>
      </section>

      {/* THE PROBLEM */}
      <section className="py-20 px-4 md:px-6 bg-[#2a3f65] text-white" data-id="element-36">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={fadeInUp} className="max-w-4xl mx-auto text-center" data-id="element-37">
          <h2 className="text-3xl md:text-4xl font-bold mb-8" data-id="element-38">
            El mercado te ha mentido
          </h2>

          <p className="text-lg md:text-xl text-gray-300 mb-12 max-w-3xl mx-auto" data-id="element-39">
            El mercado en Estados Unidos te ha hecho creer que sin un título
            universitario de aquí o un inglés fluido, tu único destino es el
            trabajo pesado. Te han dicho que para ganar en dólares tienes que
            sacrificar tu salud física.
          </p>

          <div className="bg-[#1e2d4d] p-8 rounded-2xl border border-[#3a5280] shadow-2xl" data-id="element-40">
            <p className="text-lg md:text-xl leading-relaxed" data-id="element-41">
              Mientras tanto, miles de dueños de negocios latinos en Florida,
              Texas, California y Nueva York están perdiendo dinero porque no
              tienen a nadie que les ayude a responder mensajes, agendar citas o
              llevar sus cuentas en español.
            </p>
            <p className="text-2xl md:text-3xl font-bold text-[#c9a84c] mt-6" data-id="element-42">
              Ellos no necesitan a un experto de Silicon Valley; te necesitan a
              ti.
            </p>
          </div>
        </motion.div>
      </section>

      {/* WHY WHAT YOU TRIED DIDN'T WORK */}
      <section className="py-20 px-4 md:px-6 bg-[#fdfcfa]" data-id="element-43">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={fadeInUp} className="max-w-5xl mx-auto" data-id="element-44">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-[#1e2d4d] mb-12" data-id="element-45">
            Por qué lo que intentaste antes falló
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-46">
            {["Intentaste 'llenar encuestas' por internet y solo perdiste tiempo ganando centavos.", 'Buscaste aplicaciones de entregas o transporte, pero terminaste gastando más en gasolina y desgaste de tu auto del que ganaste.', 'Trataste de entrar en plataformas de freelancers en inglés y te sentiste invisible porque no podías competir con el idioma.', "Te uniste a grupos de 'ganar dinero fácil' que resultaron ser estafas o esquemas piramidales."].map((item, index) => <div key={index} className="flex items-start gap-4 bg-white p-6 rounded-xl shadow-md border-l-4 border-red-400" data-id="element-47">
                <XCircle className="w-8 h-8 text-red-400 flex-shrink-0 mt-1" data-id="element-48" />
                <p className="text-[#1e2d4d] font-medium" data-id="element-49">{item}</p>
              </div>)}
          </div>
        </motion.div>
      </section>

      {/* UNIQUE MECHANISM */}
      <section className="py-20 px-4 md:px-6 bg-[#1e2d4d] text-white relative overflow-hidden" data-id="element-50">
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-[#c9a84c] opacity-5 rounded-full blur-3xl translate-x-1/2 -translate-y-1/2" data-id="element-51"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#c9a84c] opacity-5 rounded-full blur-3xl -translate-x-1/2 translate-y-1/2" data-id="element-52"></div>

        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={fadeInUp} className="max-w-4xl mx-auto relative z-10" data-id="element-53">
          <div className="text-center mb-12" data-id="element-54">
            <span className="inline-block px-4 py-1 rounded-full bg-[#c9a84c]/20 text-[#c9a84c] font-bold text-sm tracking-wider mb-4 border border-[#c9a84c]/30" data-id="element-55">
              LA NUEVA OPORTUNIDAD
            </span>
            <h2 className="text-3xl md:text-5xl font-bold mb-6" data-id="element-56">
              El Secreto es el{' '}
              <span className="text-[#c9a84c]" data-id="element-57">"Arbitraje de Idioma"</span>
            </h2>
          </div>

          <div className="bg-[#2a3f65]/50 backdrop-blur-sm p-8 md:p-12 rounded-2xl border border-[#c9a84c]/20" data-id="element-58">
            <p className="text-lg md:text-xl text-gray-200 leading-relaxed mb-6" data-id="element-59">
              En lugar de pelear por trabajos en inglés, nos enfocamos en el
              mercado hispano de EE.UU. que mueve billones de dólares. Tú te
              conviertes en el puente digital para empresarios latinos que
              prefieren confiar en alguien que hable su idioma y entienda su
              cultura.
            </p>
            <div className="flex items-center gap-4 p-4 bg-[#c9a84c]/10 rounded-lg border border-[#c9a84c]/30" data-id="element-60">
              <CheckCircle2 className="w-8 h-8 text-[#c9a84c] flex-shrink-0" data-id="element-61" />
              <p className="text-lg font-medium text-white" data-id="element-62">
                No necesitas tecnología compleja,{' '}
                <span className="text-[#c9a84c] font-bold" data-id="element-63">
                  solo tu capacidad de organización y el español que ya dominas
                </span>
                .
              </p>
            </div>
          </div>
        </motion.div>
      </section>
    </>;
}